import React, { useEffect, useState } from 'react';
import { useUser } from '../../../UserContext';

import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

import { Typography, TextField, Button } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import Alert from '@mui/material/Alert';

import { saveMenuData } from '../../../services/kitchenService';
import { useAuth } from '../../../AuthContext';
import { MENU_TYPE_OPTIONS, MENU_NAME_EXAMPLES_TEXT } from './common';
import { useLocale } from '../../../i18n/LanguageContext';

export default function MenuEditIconButton(props) {
  const { auth } = useAuth();
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const renderAsButton = Boolean(props.renderAsButton);
  const originalMenu = props.menu;

  const { user } = useUser();
  const [menu, setMenu] = useState(props.menu);
  const selectedMenuType = MENU_TYPE_OPTIONS.find((menuTypeOption) => menuTypeOption.value === menu.type);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMenu((prevMenu) => ({
      ...prevMenu,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setMenu({...menu, isActive: e.target.checked});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMenuData(menu, auth).then((result) => {
      if (result) {
        props.onMenuChange(menu);
        setOpen(false);
      } else {
        setError(true);
      }
    }).catch(() => {
      setError(true);
    });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setMenu(originalMenu);
    setError(false);
    setOpen(false);
  };

  return (
    <div>
      {renderAsButton ? (
        <Button
          data-testid="item-edit-id"
          variant="outlined"
          size="small"
          onClick={handleClickOpen}
          sx={(theme) => ({
            textTransform: 'none',
            backgroundColor: theme.palette.background.paper,
          })}
        >
          {t('kitchenMenus.editMenu')}
        </Button>
      ) : (
        <Tooltip key={'edit-menu'} title={t('kitchenMenus.editMenu')} placement="top-start" value="edit-menu">
          <IconButton 
                data-testid="item-edit-id" 
                color="default" 
                onClick={handleClickOpen}>
                <ModeEditOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
      
      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        { error ? (<Alert severity="error">{t('kitchenDashboard.error')}</Alert>) : ''}
        <DialogTitle id="responsive-dialog-title" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <div>{t('kitchenMenus.editMenu')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography variant="caption" style={{ fontWeight: menu.isActive ? 400 : 600 }}>{t('kitchenMenus.inactive')}</Typography>
                  <Switch 
                    name='isActive'
                    checked={menu.isActive} 
                    onChange={handleSwitchChange}
                    value={menu.isActive}
                    inputProps={{ 'aria-label': 'Menu status' }}
                  />
                  <Typography variant="caption" style={{ fontWeight: menu.isActive ? 600 : 400 }}>{t('kitchenMenus.active')}</Typography>
                </div>
            </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <form >
                <TextField
                    label={t('kitchenMenus.nameLabel')}
                    name="name"
                    value={menu.name}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
                <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 0, marginBottom: 8 }}>
                  {t('kitchenMenus.examples')}
                </Typography>

                <TextField
                    label={t('kitchenMenus.typeLabel')}
                    name="type"
                    value={menu.type}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    select
                    SelectProps={{ native: true }}
                >
                  <option value=""></option>
                  {MENU_TYPE_OPTIONS.map((menuTypeOption) => (
                    <option key={menuTypeOption.value} value={menuTypeOption.value}>
                      {t(`kitchenMenus.menuTypes.${menuTypeOption.value}`)}
                    </option>
                  ))}
                </TextField>
                {selectedMenuType ? (
                  <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 0 }}>
                    {t(`kitchenMenus.menuTypeDescriptions.${selectedMenuType.value}`)}
                  </Typography>
                ) : null}
            </form>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} style={{ textTransform: 'none'}}>
            {t('kitchenMenus.cancel')}
          </Button>
          <Button autoFocus style={{ textTransform: 'none'}} variant='contained' onClick={handleSubmit}>
            {t('kitchenMenus.saveNew')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}